import { z } from 'zod';
import { workflowRuleReplacementsSchema } from './workflow-admission';

/** Commissioning fields are validated without narrowing historical campaign JSON. */
export const crmCampaignCommissioningFieldsSchema = z.object({
  business_driver_id: z.string().uuid().nullable().optional(),
  commissioning_customer_list_required: z.boolean().optional(),
});
export type CrmCampaignCommissioningFields = z.infer<typeof crmCampaignCommissioningFieldsSchema>;

/** Existing campaign commands: preserve the open payload for unrelated CRM fields. */
export const crmCampaignCreateBodySchema = crmCampaignCommissioningFieldsSchema.extend({
  workflow_replacements: workflowRuleReplacementsSchema.optional(),
  name: z.string().max(255),
  type: z.enum([
    'general', 'marketing', 'booking', 'service', 'gtm', 'lead',
    'sms', 'whatsapp', 'email', 'outbound_call',
  ]),
}).passthrough();
export const crmCampaignUpdateBodySchema = crmCampaignCommissioningFieldsSchema.passthrough();
export type CrmCampaignCreateInput = z.infer<typeof crmCampaignCreateBodySchema>;
export type CrmCampaignUpdateInput = z.infer<typeof crmCampaignUpdateBodySchema>;

// GET /crm/gdpr/privacy-document uses a top-level `document`, not `data`.
// consent_types is historical open JSON; consumers narrow before rendering.
export const crmPrivacyDocumentResponseSchema = z.object({
  success: z.literal(true),
  document: z.object({
    id: z.string(),
    version: z.union([z.number(), z.string()]),
    title: z.string().optional(),
    content: z.string().optional(),
    published_at: z.string().nullable().optional(),
    consent_types: z.unknown().optional(),
  }).nullable(),
});
import { calendarAppointmentSchema, calendarAppointmentStatusSchema } from './calendar';

/** Existing campaign appointments-overview projection, serialized for HTTP. */
export const crmCampaignAppointmentRowSchema = calendarAppointmentSchema.pick({
  id: true, user_id: true, title: true, start_time: true, end_time: true, status: true,
  customer_name: true, customer_email: true, customer_phone: true, customer_id: true,
  service_type_id: true, type_name: true, type_color: true, appointment_form_id: true,
  form_name: true, form_slug: true, assigned_to_id: true, campaign_id: true,
  metadata: true, source_kind: true, created_at: true,
}).strip().extend({
  start_time: z.string(),
  end_time: z.string(),
  created_at: z.string(),
  status: calendarAppointmentStatusSchema,
  assigned_name: z.string().nullable(),
  metadata_source: z.string(),
  metadata_mcp_tool_name: z.string(),
  metadata_created_by_user_name: z.string(),
  created_by_ai: z.boolean(),
});
export type CrmCampaignAppointmentRow = z.infer<typeof crmCampaignAppointmentRowSchema>;
export const crmCampaignAppointmentsOverviewSchema = z.object({
  kpi: z.object({
    total: z.number(), in_agenda: z.number(), completed: z.number(), no_show_rate: z.number(),
  }),
  appointments: z.array(crmCampaignAppointmentRowSchema),
  pagination: z.object({
    page: z.number(), limit: z.number(), total: z.number(), total_pages: z.number(),
  }),
});
export type CrmCampaignAppointmentsOverview = z.infer<typeof crmCampaignAppointmentsOverviewSchema>;

// Task #1503: nominativi clienti/lead in UPPERCASE (transform Zod non bypassabile).
const upperOptional = (max: number) =>
  z.string().max(max).transform((v) => v.toLocaleUpperCase('it-IT')).optional();
const upperNullableOptional = (max: number) =>
  z
    .string()
    .max(max)
    .transform((v) => v.toLocaleUpperCase('it-IT'))
    .nullable()
    .optional();

export const createLeadBodySchema = z.object({
  campaign_id: z.string().uuid('Campagna obbligatoria: ID campagna non valido'),
  contact_first_name: z
    .string()
    .min(1)
    .max(255)
    .transform((v) => v.toLocaleUpperCase('it-IT'))
    .optional(),
  contact_last_name: z
    .string()
    .min(1)
    .max(255)
    .transform((v) => v.toLocaleUpperCase('it-IT'))
    .optional(),
  contact_email: z.string().email('Formato email non valido').max(255).optional().nullable(),
  contact_phone: z.string().max(50).optional().nullable(),
  company_name: upperNullableOptional(500),
  vat_number: z.string().max(50).optional().nullable(),
  lead_type: z.enum(['b2c', 'b2b']).optional(),
  legal_form: z.string().max(50).optional().nullable(),
  sdi_code: z.string().max(20).optional().nullable(),
  pec_email: z.string().email('Formato email non valido').max(255).optional().nullable(),
  company_email: z.string().email('Formato email non valido').max(255).optional().nullable(),
  company_phone: z.string().max(50).optional().nullable(),
  website: z.string().max(500).optional().nullable(),
  sector: z.string().max(255).optional().nullable(),
  employee_count: z.string().max(50).optional().nullable(),
  annual_revenue: z.string().max(100).optional().nullable(),
  tax_code: z.string().max(50).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
  source: z.string().max(100).optional().nullable(),
  stage_id: z.string().uuid().optional(),
  assigned_to: z.string().uuid().optional().nullable(),
  person_id: z.string().uuid().optional().nullable(),
}).passthrough().superRefine((data, ctx) => {
  // Lead B2B: solo Ragione sociale + Partita IVA + Forma giuridica obbligatori.
  // Vincolo applicato al boundary HTTP (modale manuale); l'import Apollo non passa
  // da qui e può creare lead B2B incompleti da arricchire.
  if (data.lead_type === 'b2b') {
    // Trim: stringhe di soli spazi non soddisfano l'obbligatorietà.
    if (!data.company_name || !data.company_name.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['company_name'], message: 'Ragione sociale obbligatoria per lead B2B' });
    }
    if (!data.vat_number || !data.vat_number.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vat_number'], message: 'Partita IVA obbligatoria per lead B2B' });
    }
    if (!data.legal_form || !data.legal_form.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['legal_form'], message: 'Forma giuridica obbligatoria per lead B2B' });
    }
  }
});
export type CreateLeadBody = z.infer<typeof createLeadBodySchema>;

export const updateLeadStageBodySchema = z.object({
  stage_id: z.string().uuid('Stage ID obbligatorio'),
}).passthrough();
export type UpdateLeadStageBody = z.infer<typeof updateLeadStageBodySchema>;

export const createPipelineEntryBodySchema = z.object({
  pipeline_id: z.string().uuid('Pipeline ID obbligatorio').optional(),
  stage_id: z.string().uuid('Stage ID obbligatorio').optional(),
  company_name: z
    .string()
    .min(1, 'Ragione sociale obbligatoria')
    .max(500)
    .transform((v) => v.toLocaleUpperCase('it-IT'))
    .optional(),
  vat_number: z.string().max(50).optional().nullable(),
  contact_name: upperNullableOptional(255),
  contact_first_name: upperNullableOptional(255),
  contact_last_name: upperNullableOptional(255),
  contact_email: z.string().email('Formato email non valido').max(255).optional().nullable(),
  contact_phone: z.string().max(50).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
}).passthrough();
export type CreatePipelineEntryBody = z.infer<typeof createPipelineEntryBodySchema>;

// Task #1503: schema condiviso per UPDATE master_pipeline_entries / clients master.
// Usato dalle rotte PUT /master/crm/clients/:id e PUT /master/crm/pipeline/entries/:id.
export const updateMasterCustomerBodySchema = z
  .object({
    company_name: upperNullableOptional(500),
    contact_name: upperNullableOptional(255),
    contact_first_name: upperNullableOptional(255),
    contact_last_name: upperNullableOptional(255),
  })
  .passthrough();
export type UpdateMasterCustomerBody = z.infer<typeof updateMasterCustomerBodySchema>;

// Task #3056 — CRM Master modello B: anagrafica cliente + referenti master-native.
const masterCustomerCoreShape = {
  customer_type: z.enum(['b2b', 'b2c']).optional(),
  company_name: upperNullableOptional(500),
  contact_name: upperNullableOptional(255),
  contact_first_name: upperNullableOptional(255),
  contact_last_name: upperNullableOptional(255),
  contact_email: z.string().email('Formato email non valido').max(255).optional().nullable(),
  contact_phone: z.string().max(50).optional().nullable(),
  vat_number: z.string().max(50).optional().nullable(),
  fiscal_code: z.string().max(50).optional().nullable(),
  sdi_code: z.string().max(20).optional().nullable(),
  pec: z.string().max(255).optional().nullable(),
  legal_address: z.string().max(500).optional().nullable(),
  legal_city: z.string().max(255).optional().nullable(),
  legal_province: z.string().max(10).optional().nullable(),
  legal_zip: z.string().max(20).optional().nullable(),
  legal_country: z.string().max(100).optional().nullable(),
  legal_form: z.string().max(50).optional().nullable(),
  iban: z.string().max(50).optional().nullable(),
  bank_name: z.string().max(255).optional().nullable(),
  sector: z.string().max(255).optional().nullable(),
  client_notes: z.string().max(5000).optional().nullable(),
} as const;

// Referente (settore/ruolo) di un cliente master.
export const masterCustomerContactBodySchema = z.object({
  first_name: upperNullableOptional(255),
  last_name: upperNullableOptional(255),
  role: z.string().max(255).optional().nullable(),
  sector: z.string().max(255).optional().nullable(),
  email: z.string().email('Formato email non valido').max(255).optional().nullable(),
  phone: z.string().max(50).optional().nullable(),
  mobile: z.string().max(50).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
  is_primary: z.boolean().optional(),
});
export type MasterCustomerContactBody = z.infer<typeof masterCustomerContactBodySchema>;

// Creazione cliente master (CRUD anagrafica). Referenti opzionali inline.
export const createMasterCustomerBodySchema = z.object({
  ...masterCustomerCoreShape,
  contacts: z.array(masterCustomerContactBodySchema).optional(),
});
export type CreateMasterCustomerBody = z.infer<typeof createMasterCustomerBodySchema>;

// Conversione lead → cliente: collega (customer_id) OPPURE crea (campi anagrafici).
export const convertLeadToClientBodySchema = z.object({
  ...masterCustomerCoreShape,
  customer_id: z.string().uuid().optional().nullable(),
  contacts: z.array(masterCustomerContactBodySchema).optional(),
});
export type ConvertLeadToClientBody = z.infer<typeof convertLeadToClientBodySchema>;

// Schema per master/users (POST/PATCH): solo first_name/last_name uppercase.
// password/email/role validati a parte nel route handler esistente.
export const masterUserNamePartialSchema = z
  .object({
    first_name: upperOptional(100),
    last_name: upperOptional(100),
  })
  .passthrough();
export type MasterUserNamePartial = z.infer<typeof masterUserNamePartialSchema>;

// ============================================================================
// Task #3653 — Contratto dati unico BE↔FE: schemi Zod di RISPOSTA CRM.
// Tipi derivati con `z.infer`, SoT unica: il backend (crm.service) li usa come
// tipi di ritorno, il FE come generic di `apiClient.get<T>`.
// Vedere docs/zod-boundary.md § Risposte.
// ============================================================================

export const crmStageTypeSchema = z.enum(['entry', 'wip', 'appointment', 'won', 'lost', 'archived']);
export type CrmStageType = z.infer<typeof crmStageTypeSchema>;

/**
 * Riga `crm_pipeline_stages` (GET /crm/pipeline-stages ritorna l'array raw).
 * `created_at`/`updated_at`: Date lato service (driver pg), stringa ISO sul
 * wire JSON → union documentata al boundary.
 */
export const crmPipelineStageSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  name: z.string(),
  color: z.string(),
  position: z.number(),
  is_won: z.boolean(),
  is_lost: z.boolean(),
  is_default: z.boolean(),
  is_archived_stage: z.boolean(),
  stage_type: crmStageTypeSchema,
  created_at: z.union([z.string(), z.date()]),
  updated_at: z.union([z.string(), z.date()]),
});
export type CrmPipelineStage = z.infer<typeof crmPipelineStageSchema>;

/**
 * GET /crm/dashboard — stats aggregate. `recentLeads` è `crm_leads.*` + campi
 * denormalizzati stage: modellato con i campi consumati + passthrough (il
 * dettaglio lead completo resta fuori scope del contratto dashboard).
 */
export const crmDashboardRecentLeadSchema = z
  .object({
    id: z.string(),
    title: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    created_at: z.union([z.string(), z.date()]),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    contact_email: z.string().nullable().optional(),
    contact_phone: z.string().nullable().optional(),
    stage_name: z.string().nullable(),
    stage_color: z.string().nullable(),
  })
  .passthrough();
export type CrmDashboardRecentLead = z.infer<typeof crmDashboardRecentLeadSchema>;

export const crmDashboardStatsSchema = z.object({
  contacts: z.number(),
  companies: z.number(),
  leads: z.number(),
  activeCampaigns: z.number(),
  leadsByStage: z.array(z.object({
    id: z.string(),
    name: z.string(),
    color: z.string(),
    count: z.number(),
  })),
  recentLeads: z.array(crmDashboardRecentLeadSchema),
});
export type CrmDashboardStats = z.infer<typeof crmDashboardStatsSchema>;
